"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  Clock,
  ExternalLink,
  ShieldCheck,
  TrendingUp,
  Sparkles,
  Zap,
  Building2,
  CheckCircle2,
  ArrowRight,
  Calculator,
  MessageSquare,
  Mail,
  FileText,
  Lock,
  Layers,
  Award,
  DollarSign,
  HelpCircle,
} from "lucide-react";
import { PITCH_PRICING_DATA, NEGOTIATION_RULES } from "@/data/pitchDeckData";

export default function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState<number>(1);
  const totalSlides = 10;
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [secondsElapsed, setSecondsElapsed] = useState<number>(0);

  // ROI Calculator State
  const [monthlyLeads, setMonthlyLeads] = useState<number>(80);
  const [dealSizeInr, setDealSizeInr] = useState<number>(250000); // 2.5L avg
  const [dealSizeUsd, setDealSizeUsd] = useState<number>(3500); // $3.5k avg
  const [currentConversion, setCurrentConversion] = useState<number>(4); // 4%

  // Presentation meeting stopwatch
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Keyboard navigation
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev < totalSlides ? prev + 1 : prev));
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || (e.key === " " && !e.shiftKey)) {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp" || (e.key === " " && e.shiftKey)) {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "Home") {
        e.preventDefault();
        setCurrentSlide(1);
      } else if (e.key === "End") {
        e.preventDefault();
        setCurrentSlide(totalSlides);
      } else if (e.key.toLowerCase() === "f") {
        toggleFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, totalSlides]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  // ROI Math
  const activeDealSize = currency === "INR" ? dealSizeInr : dealSizeUsd;
  const currentDealsWon = Math.round((monthlyLeads * currentConversion) / 100);
  // An instant response increases conversion by ~60% to 150% in B2B
  const improvedConversion = Math.min(Math.round(currentConversion * 1.8 * 10) / 10, 25);
  const newDealsWon = Math.round((monthlyLeads * improvedConversion) / 100);
  const extraDeals = Math.max(newDealsWon - currentDealsWon, 1);
  const extraMonthlyRevenue = extraDeals * activeDealSize;
  const extraAnnualRevenue = extraMonthlyRevenue * 12;

  return (
    <div className="min-h-screen bg-[#07090e] text-slate-100 flex flex-col justify-between selection:bg-cyan-500 selection:text-black font-sans">
      {/* TOP CONTROL BAR */}
      <header className="h-16 border-b border-slate-800/80 bg-[#0a0d14]/90 backdrop-blur-md px-4 sm:px-8 flex items-center justify-between z-50">
        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-cyan-500 to-indigo-600 flex items-center justify-center font-bold text-black text-sm shadow-md shadow-cyan-500/20 group-hover:scale-105 transition-transform">
              JS
            </div>
            <span className="font-semibold tracking-tight text-white hidden md:inline">
              Johnson Softwares <span className="text-cyan-400 font-normal">| Executive Pitch</span>
            </span>
          </Link>

          <div className="h-4 w-[1px] bg-slate-800 hidden sm:block"></div>

          {/* Meeting Stopwatch */}
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-900/90 border border-slate-800 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5 text-cyan-400" />
            <span className="font-mono text-slate-300">{formatTimer(secondsElapsed)}</span>
          </div>
        </div>

        {/* Slide Selector & Currency Toggle */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Currency Pill */}
          <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5">
            <button
              onClick={() => setCurrency("INR")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                currency === "INR"
                  ? "bg-cyan-500 text-black font-semibold shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              INR (₹)
            </button>
            <button
              onClick={() => setCurrency("USD")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                currency === "USD"
                  ? "bg-cyan-500 text-black font-semibold shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              USD ($)
            </button>
          </div>

          {/* Slide Indicator */}
          <div className="px-3 py-1 bg-slate-900/80 border border-slate-800 rounded-lg text-xs font-medium text-slate-300">
            Slide <span className="text-cyan-400 font-bold">{currentSlide}</span> of {totalSlides}
          </div>

          {/* Fullscreen button */}
          <button
            onClick={toggleFullscreen}
            className="p-2 text-slate-400 hover:text-white bg-slate-900 border border-slate-800 rounded-lg transition-colors hidden sm:block"
            title="Toggle Fullscreen (F)"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* MAIN SLIDE STAGE */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8 md:p-12 relative overflow-hidden">
        {/* Background Ambience Glow */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none -z-10"></div>

        <div className="w-full max-w-6xl mx-auto">
          {/* ========================================================================= */}
          {/* SLIDE 1: TITLE & HOOK */}
          {/* ========================================================================= */}
          {currentSlide === 1 && (
            <div className="space-y-8 text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium tracking-wide uppercase">
                <Sparkles className="w-3.5 h-3.5" /> High-Velocity Enterprise Multi-Tenant Solution
              </div>

              <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-white max-w-4xl mx-auto leading-[1.1]">
                From Clicks to Contracts:
                <span className="block bg-gradient-to-r from-cyan-400 via-teal-300 to-indigo-400 bg-clip-text text-transparent">
                  The Autonomous CRM
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto font-light leading-relaxed">
                Why generic CRMs bleed <span className="text-white font-semibold">68% of warm inbound leads</span> — and how custom dual-channel automation locks in revenue within 2 seconds of intent.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto pt-6">
                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 text-left">
                  <div className="text-3xl font-black text-cyan-400">&lt; 2 Seconds</div>
                  <div className="text-sm font-medium text-white mt-1">Dual-Channel Response Velocity</div>
                  <div className="text-xs text-slate-400 mt-1">Instant WhatsApp notification + PDF agreement delivered simultaneously.</div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 text-left">
                  <div className="text-3xl font-black text-indigo-400">391% Lift</div>
                  <div className="text-sm font-medium text-white mt-1">Inbound Contact Rate</div>
                  <div className="text-xs text-slate-400 mt-1">First-responder advantage capturing buyers before competitors even open their inbox.</div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 text-left">
                  <div className="text-3xl font-black text-emerald-400">100% Isolated</div>
                  <div className="text-sm font-medium text-white mt-1">Dedicated Tenant Architecture</div>
                  <div className="text-xs text-slate-400 mt-1">Bank-grade data sovereignty with zero cross-tenant contamination.</div>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-center gap-3 text-xs text-slate-500">
                <span>Press <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-300 font-mono">→</kbd> or click Next to explore</span>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 2: THE PROBLEM (THE INBOUND DECAY CURVE) */}
          {/* ========================================================================= */}
          {currentSlide === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-800 pb-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-rose-400">The Silent Revenue Bleed</span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">The 5-Minute Inbound Lead Decay Curve</h2>
                </div>
                <div className="text-sm text-slate-400 max-w-sm">
                  Harvard Business Review: Firms that contact prospects within 5 minutes are <span className="text-white font-bold">21x more likely</span> to qualify them than those who wait 30 minutes.
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                {/* Visual Decay Bar Chart */}
                <div className="lg:col-span-8 p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-5">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-cyan-400 flex items-center gap-2">
                        <Zap className="w-4 h-4" /> &lt; 2 Minutes (Johnson Softwares Dual Dispatch)
                      </span>
                      <span className="font-bold text-cyan-300">100% Baseline Conversion</span>
                    </div>
                    <div className="h-6 w-full bg-slate-800 rounded-full overflow-hidden p-0.5">
                      <div className="h-full bg-gradient-to-r from-cyan-500 to-teal-400 rounded-full w-full"></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-300">30 Minutes (Manual Sales Rep Alert)</span>
                      <span className="font-semibold text-amber-400">48% Conversion (-52% Lost)</span>
                    </div>
                    <div className="h-6 w-full bg-slate-800 rounded-full overflow-hidden p-0.5">
                      <div className="h-full bg-amber-500/80 rounded-full w-[48%]"></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-300">4 Hours (Standard Industry Average)</span>
                      <span className="font-semibold text-rose-400">14% Conversion (-86% Lost)</span>
                    </div>
                    <div className="h-6 w-full bg-slate-800 rounded-full overflow-hidden p-0.5">
                      <div className="h-full bg-rose-500/80 rounded-full w-[14%]"></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-400">24 Hours (Next Day Follow-up)</span>
                      <span className="font-semibold text-rose-500">4% Conversion (-96% Lost)</span>
                    </div>
                    <div className="h-6 w-full bg-slate-800 rounded-full overflow-hidden p-0.5">
                      <div className="h-full bg-rose-900/60 rounded-full w-[4%]"></div>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300 flex items-center gap-2">
                    <span className="font-bold">Reality:</span> 78% of customers buy from the vendor that contacts them first. Slow follow-up is handing revenue straight to your competitors.
                  </div>
                </div>

                {/* Impact stats */}
                <div className="lg:col-span-4 space-y-4">
                  <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div className="text-xs uppercase text-slate-400 font-semibold">The Cost of Manual CRM</div>
                    <div className="text-2xl font-bold text-white mt-1">₹4.2L / $5,000+</div>
                    <div className="text-xs text-slate-400 mt-1">Average monthly revenue lost per sales rep due to delayed lead touches and dropped follow-ups.</div>
                  </div>

                  <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div className="text-xs uppercase text-slate-400 font-semibold">The Customer Expectation</div>
                    <div className="text-2xl font-bold text-cyan-400 mt-1">Instant WhatsApp Verification</div>
                    <div className="text-xs text-slate-400 mt-1">93% of modern buyers check WhatsApp within 3 minutes of submitting an inquiry.</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 3: THE SOLUTION (DUAL OMNICHANNEL ENGINE) */}
          {/* ========================================================================= */}
          {currentSlide === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div className="border-b border-slate-800 pb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Architecture Breakdown</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">The Dual Omnichannel Dispatch Engine</h2>
                <p className="text-sm text-slate-400 mt-1">How client intent converts into binding commitments in real-time across Web, Email, and WhatsApp.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 1 */}
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-bold mb-4">
                      1
                    </div>
                    <h3 className="text-lg font-bold text-white">Client Portal & Fee Calculator</h3>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                      Prospect selects custom requirements on the interactive portal. High-intent parameters (budget, shift hours, treatment plan, or candidate CTC) are calculated live.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-800 text-xs font-mono text-cyan-400">
                    → Instant Ingestion POST
                  </div>
                </div>

                {/* Step 2 */}
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold mb-4">
                      2
                    </div>
                    <h3 className="text-lg font-bold text-white">Interactive Pipeline Kanban</h3>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                      Lead enters the operator CRM. Dragging a card across stages triggers automated state machine transitions with dedicated action modals and audit logging.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-800 text-xs font-mono text-indigo-400">
                    → Stage Event PATCH
                  </div>
                </div>

                {/* Step 3 */}
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-cyan-500/30 shadow-lg shadow-cyan-500/5 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold mb-4">
                      3
                    </div>
                    <h3 className="text-lg font-bold text-white">Simultaneous Dual Dispatch</h3>
                    <div className="mt-3 space-y-2 text-xs">
                      <div className="p-2.5 rounded bg-slate-950 border border-slate-800 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                        <div>
                          <span className="font-bold text-slate-200">Hostinger SMTP Email</span>
                          <p className="text-slate-400 text-[11px]">Dynamic PDF contract / invoice attached.</p>
                        </div>
                      </div>
                      <div className="p-2.5 rounded bg-slate-950 border border-slate-800 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-emerald-400 shrink-0" />
                        <div>
                          <span className="font-bold text-slate-200">Meta WhatsApp Cloud API</span>
                          <p className="text-slate-400 text-[11px]">Instant mobile notification with reference code.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-800 text-xs font-mono text-emerald-400">
                    → &lt; 2 Second Delivery SLA
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 4: 7 SPECIALIZED INDUSTRY VERTICALS */}
          {/* ========================================================================= */}
          {currentSlide === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 border-b border-slate-800 pb-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Turnkey Solutions</span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">7 Production-Ready Industry CRMs</h2>
                </div>
                <div className="text-xs text-slate-400">
                  Click any demo to test live in a separate window.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                {PITCH_PRICING_DATA.map((item) => (
                  <div key={item.id} className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 hover:border-cyan-500/40 transition-all flex flex-col justify-between group">
                    <div>
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="px-2 py-0.5 rounded bg-slate-800 text-cyan-400 font-mono">{item.badge}</span>
                        <span className="text-slate-500 font-mono text-[11px]">Ready</span>
                      </div>
                      <h4 className="font-bold text-white text-sm group-hover:text-cyan-300 transition-colors leading-snug">{item.name}</h4>
                      <ul className="mt-2.5 space-y-1 text-[11px] text-slate-400">
                        {item.keyAssets.slice(0, 2).map((asset, idx) => (
                          <li key={idx} className="flex items-center gap-1.5 truncate">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                            <span className="truncate">{asset}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between gap-2">
                      <Link
                        href={item.demoUrl}
                        target="_blank"
                        className="text-[11px] font-medium text-slate-300 hover:text-white flex items-center gap-1"
                      >
                        Portal <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                      <Link
                        href={item.crmUrl}
                        target="_blank"
                        className="text-[11px] font-semibold text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      >
                        Pipeline CRM <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 5: HIGH-TICKET ASSETS & PDF INVOICING */}
          {/* ========================================================================= */}
          {currentSlide === 5 && (
            <div className="space-y-6 animate-fade-in">
              <div className="border-b border-slate-800 pb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-purple-400">High-Ticket Deal Closing</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">Dynamic Contracts & Regulatory PDFs</h2>
                <p className="text-sm text-slate-400 mt-1">Clients commit faster when formal, bank-grade documents arrive instantly in their inbox.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 space-y-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-white">Prestige Real Estate</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    VIP Chauffeur & Site Visit Itinerary PDF + Official Booking Token Tax Invoice. Outlines exact plot dimensions, unit number, and wire coordinates.
                  </p>
                  <div className="text-[11px] text-amber-400 font-mono bg-amber-500/10 p-2 rounded">
                    Avg Ticket: ₹2.5 Cr / $350k
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 space-y-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                    <FileText className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-white">Vocalis Contact Centers & BPO</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Master Services Agreement (MSA) + Seat Retainer Tax Invoice PDF. Specifies agent headcount, shift hours, telephony SLA, and billing cycle.
                  </p>
                  <div className="text-[11px] text-indigo-400 font-mono bg-indigo-500/10 p-2 rounded">
                    Avg Ticket: ₹1.6L/mo / $2k/mo
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 space-y-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                    <Award className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-white">Nexus Executive Search</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Candidate Evaluation Dossier PDF + 20-25% Executive Placement Fee Invoice. Features vetted competency scores, panel schedule, and warranty clauses.
                  </p>
                  <div className="text-[11px] text-purple-400 font-mono bg-purple-500/10 p-2 rounded">
                    Avg Ticket: ₹31.25L / $37.5k
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 6: MULTI-TENANT ARCHITECTURE & DATA ISOLATION */}
          {/* ========================================================================= */}
          {currentSlide === 6 && (
            <div className="space-y-6 animate-fade-in">
              <div className="border-b border-slate-800 pb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Enterprise Security & Compliance</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">Dedicated Tenant Isolation Architecture</h2>
                <p className="text-sm text-slate-400 mt-1">How we guarantee zero data leaks, GDPR/HIPAA compatibility, and custom deployment options.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Option 1 */}
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-cyan-500/40 relative">
                  <div className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-semibold">
                    Recommended for Enterprise
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-4">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Dedicated Managed Container</h3>
                  <p className="text-xs text-slate-400 mt-1">Each client gets an isolated Docker instance with its own database.</p>

                  <ul className="mt-4 space-y-2 text-xs text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span><strong>100% Data Sovereignty:</strong> Dedicated SQLite / Postgres volume.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span><strong>Custom Subdomain:</strong> crm.clientcompany.com with dedicated SSL.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span><strong>Zero Cross-Contamination:</strong> Guaranteed zero risk of tenant data leak.</span>
                    </li>
                  </ul>
                </div>

                {/* Option 2 */}
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-4">
                    <Layers className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Multi-Tenant Schema (org_id)</h3>
                  <p className="text-xs text-slate-400 mt-1">Cost-effective shared cluster with row-level security isolation.</p>

                  <ul className="mt-4 space-y-2 text-xs text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0" />
                      <span><strong>High Capital Efficiency:</strong> Lower monthly operating overhead.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0" />
                      <span><strong>JWT Tenant Guard:</strong> API middleware enforces strict org_id filtering.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0" />
                      <span><strong>Instant Provisioning:</strong> New workspace active in under 60 seconds.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 7: INTERACTIVE CLIENT ROI CALCULATOR */}
          {/* ========================================================================= */}
          {currentSlide === 7 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 border-b border-slate-800 pb-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Financial Impact</span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">Live Revenue ROI Simulator</h2>
                </div>
                <div className="text-xs text-slate-400">
                  Adjust parameters live on call to show client projected payoff.
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                {/* Sliders Box */}
                <div className="lg:col-span-6 p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-5">
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-2">
                      <span className="text-slate-300">Inbound Leads per Month:</span>
                      <span className="text-cyan-400 font-mono text-sm">{monthlyLeads} Leads</span>
                    </div>
                    <input
                      type="range"
                      min="15"
                      max="300"
                      step="5"
                      value={monthlyLeads}
                      onChange={(e) => setMonthlyLeads(Number(e.target.value))}
                      className="w-full accent-cyan-500 cursor-pointer"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-2">
                      <span className="text-slate-300">Average Deal / Ticket Value:</span>
                      <span className="text-cyan-400 font-mono text-sm">
                        {currency === "INR" ? `₹${(dealSizeInr / 100000).toFixed(2)} Lakh` : `$${dealSizeUsd.toLocaleString()}`}
                      </span>
                    </div>
                    {currency === "INR" ? (
                      <input
                        type="range"
                        min="25000"
                        max="2000000"
                        step="25000"
                        value={dealSizeInr}
                        onChange={(e) => setDealSizeInr(Number(e.target.value))}
                        className="w-full accent-cyan-500 cursor-pointer"
                      />
                    ) : (
                      <input
                        type="range"
                        min="500"
                        max="30000"
                        step="500"
                        value={dealSizeUsd}
                        onChange={(e) => setDealSizeUsd(Number(e.target.value))}
                        className="w-full accent-cyan-500 cursor-pointer"
                      />
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-2">
                      <span className="text-slate-300">Current Manual Close Rate:</span>
                      <span className="text-cyan-400 font-mono text-sm">{currentConversion}%</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="12"
                      step="0.5"
                      value={currentConversion}
                      onChange={(e) => setCurrentConversion(Number(e.target.value))}
                      className="w-full accent-cyan-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Output Card */}
                <div className="lg:col-span-6 p-6 rounded-2xl bg-gradient-to-br from-cyan-950/40 via-slate-900 to-indigo-950/40 border border-cyan-500/30 space-y-4">
                  <div className="text-xs uppercase tracking-wider text-cyan-400 font-bold flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4" /> Projected Revenue Lift (Instant &lt; 2s Touch)
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <div className="text-xs text-slate-400">Additional Deals / Mo</div>
                      <div className="text-3xl font-black text-white mt-1">+{extraDeals} Deals</div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">Extra Monthly Revenue</div>
                      <div className="text-3xl font-black text-cyan-400 mt-1">
                        {currency === "INR"
                          ? `₹${(extraMonthlyRevenue / 100000).toFixed(2)}L`
                          : `$${extraMonthlyRevenue.toLocaleString()}`}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-800">
                    <div className="text-xs text-slate-400">Additional Net Revenue Per Year</div>
                    <div className="text-4xl font-extrabold text-emerald-400 mt-1">
                      {currency === "INR"
                        ? `₹${(extraAnnualRevenue / 10000000).toFixed(2)} Cr`
                        : `$${extraAnnualRevenue.toLocaleString()}`}
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      💡 Even at minimum close targets, the system delivers <strong className="text-white">10x to 35x ROI</strong> within the first quarter.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 8: COMMERCIAL PRICING MATRIX */}
          {/* ========================================================================= */}
          {currentSlide === 8 && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 border-b border-slate-800 pb-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Commercial Strategy</span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">Investment & Pricing Framework</h2>
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  Showing in {currency}
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60 max-h-[380px] overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900 text-slate-400 uppercase font-semibold sticky top-0">
                    <tr>
                      <th className="p-3">Industry Vertical</th>
                      <th className="p-3">Anchor Quote (Initial Offer)</th>
                      <th className="p-3">Target Close Price</th>
                      <th className="p-3 text-rose-400">Walkaway Floor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/80">
                    {PITCH_PRICING_DATA.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-800/40 transition-colors">
                        <td className="p-3 font-semibold text-white">
                          {p.name}
                          <span className="block text-[10px] text-slate-400 font-normal">{p.badge}</span>
                        </td>
                        <td className="p-3 font-mono text-cyan-300">
                          {currency === "INR" ? `${p.setupInr} + ${p.monthlyInr}` : `${p.setupUsd} + ${p.monthlyUsd}`}
                        </td>
                        <td className="p-3 font-mono font-bold text-emerald-400">
                          {currency === "INR" ? p.targetCloseInr : p.targetCloseUsd}
                        </td>
                        <td className="p-3 font-mono text-slate-400">
                          {currency === "INR" ? p.walkawayInr : p.walkawayUsd}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-slate-400 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <span>Anchor Quote leaves 20-30% buffer for strategic concessions while keeping profit margins strictly above 70%.</span>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 9: NEGOTIATION PLAYBOOK */}
          {/* ========================================================================= */}
          {currentSlide === 9 && (
            <div className="space-y-6 animate-fade-in">
              <div className="border-b border-slate-800 pb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">Sales Closing Strategy</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">The 3-Rule Negotiation Playbook</h2>
                <p className="text-sm text-slate-400 mt-1">How our sales team defends margins while making prospects feel like they won the negotiation.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {NEGOTIATION_RULES.map((rule, idx) => (
                  <div key={idx} className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 flex flex-col justify-between">
                    <div>
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-xs mb-3">
                        0{idx + 1}
                      </div>
                      <h3 className="font-bold text-white text-base leading-snug">{rule.rule}</h3>
                      <p className="text-xs text-slate-300 mt-3 leading-relaxed">
                        {rule.desc}
                      </p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-800">
                      <span className="text-[11px] font-semibold text-emerald-400 block">
                        ★ {rule.benefit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SLIDE 10: TURNKEY ONBOARDING & ACTION */}
          {/* ========================================================================= */}
          {currentSlide === 10 && (
            <div className="space-y-8 text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium tracking-wide uppercase">
                <CheckCircle2 className="w-3.5 h-3.5" /> 72-Hour Turnkey Deployment
              </div>

              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white max-w-3xl mx-auto leading-tight">
                Ready to Deploy in
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent"> 3 Business Days</span>
              </h2>

              {/* 3-Day Roadmap */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800">
                  <div className="text-xs font-mono text-cyan-400">DAY 01</div>
                  <div className="font-bold text-white text-sm mt-1">Tenant Setup & API Linking</div>
                  <div className="text-xs text-slate-400 mt-1">Docker instance spun up, Meta Cloud API linked to official phone number.</div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800">
                  <div className="text-xs font-mono text-indigo-400">DAY 02</div>
                  <div className="font-bold text-white text-sm mt-1">Branding & Pipeline Mapping</div>
                  <div className="text-xs text-slate-400 mt-1">Custom PDF letterheads, stages, and tax invoice schemas configured.</div>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800">
                  <div className="text-xs font-mono text-emerald-400">DAY 03</div>
                  <div className="font-bold text-white text-sm mt-1">Live Testing & Staff Onboarding</div>
                  <div className="text-xs text-slate-400 mt-1">End-to-end verification, CRM operator walkthrough, and production rollout.</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link
                  href="/contact"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-black font-bold text-sm shadow-lg shadow-cyan-500/25 transition-all flex items-center justify-center gap-2"
                >
                  Schedule Scoping Call <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  href="/demos"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm border border-slate-700 transition-all flex items-center justify-center gap-2"
                >
                  Explore All 7 Live Demos <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              <div className="text-xs text-slate-500 pt-2">
                Need a live WhatsApp test right now? Ask our engineer on call to trigger an instant stage alert to your mobile number.
              </div>
            </div>
          )}
        </div>
      </main>

      {/* BOTTOM NAVIGATION CONTROLS */}
      <footer className="h-16 border-t border-slate-800/80 bg-[#0a0d14]/90 backdrop-blur-md px-4 sm:px-8 flex items-center justify-between z-50">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 1}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
            currentSlide === 1
              ? "text-slate-600 cursor-not-allowed"
              : "text-white bg-slate-800 hover:bg-slate-700"
          }`}
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>

        {/* Slide navigation pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto max-w-[60%] py-1">
          {Array.from({ length: totalSlides }, (_, i) => i + 1).map((slideNum) => (
            <button
              key={slideNum}
              onClick={() => setCurrentSlide(slideNum)}
              className={`w-7 h-7 rounded-md text-xs font-medium transition-all ${
                currentSlide === slideNum
                  ? "bg-cyan-500 text-black font-bold shadow-md shadow-cyan-500/20 scale-110"
                  : "bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              {slideNum}
            </button>
          ))}
        </div>

        <button
          onClick={nextSlide}
          disabled={currentSlide === totalSlides}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
            currentSlide === totalSlides
              ? "text-slate-600 cursor-not-allowed"
              : "text-black bg-cyan-500 hover:bg-cyan-400 shadow-md shadow-cyan-500/20 font-bold"
          }`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </footer>
    </div>
  );
}
